import { create } from 'zustand';
import securePersist from '@/utils/securePersist';
import type { IQuestion } from '@/types/features/questionnaire/question';
import { segment } from '@/lib/analytics/segment';

export interface AnswersProps {
  question_id: string;
  answer: string;
}

export interface SelectedAnswers {
  [questionId: string]: string[];
}

interface PreQuestionnaireState {
  answers: AnswersProps[];
  questions: IQuestion[];
  currentQuestionIndex: number;
  currentQuestionAnswer: SelectedAnswers;
  // Actions
  initialize: (questions: IQuestion[]) => void;
  setCurrentQuestionAnswer: (questionId: string, answer: string[]) => void;
  addOrUpdateAnswer: (newAnswer: AnswersProps) => void;
  goToNextQuestion: () => void;
  goToPreviousQuestion: () => void;
  trackQuestionAnswered: (currentQuestion: IQuestion, currentAnswer: string[]) => void;
  clear: () => void;
}

export const preQuestionnaireStore = create<PreQuestionnaireState>()(
  securePersist(
    (set, get) => {
      // Helper to get a stored answer for a given question id.
      const getStoredAnswer = (questionId: string) =>
        get().answers.find((a) => a.question_id === questionId)?.answer || '';

      return {
        answers: [],
        questions: [],
        currentQuestionIndex: 0,
        currentQuestionAnswer: {},
        initialize: (questions) => {
          const storedAnswers = get().answers;
          // Find the first unanswered question, defaulting to index 0 if none found.
          const initialIndex =
            storedAnswers.length > 0
              ? questions.findIndex((q) => !storedAnswers.some((a) => a.question_id === q.id))
              : 0;
          // If all questions have been answered, default to the last one.
          const clampedIndex = initialIndex === -1 ? questions.length - 1 : initialIndex;
          const currentQ = questions[clampedIndex];
          const currentAnswer = currentQ ? getStoredAnswer(currentQ.id) : '';
          set({
            questions,
            currentQuestionIndex: clampedIndex,
            currentQuestionAnswer: currentQ
              ? { [currentQ.id]: currentAnswer.length ? currentAnswer.split(',') : [] }
              : {},
          });
        },
        setCurrentQuestionAnswer: (questionId, answer) =>
          set((state) => ({
            currentQuestionAnswer: { ...state.currentQuestionAnswer, [questionId]: answer },
          })),
        addOrUpdateAnswer: (newAnswer) =>
          set((state) => ({
            answers: state.answers.some((a) => a.question_id === newAnswer.question_id)
              ? state.answers.map((a) => (a.question_id === newAnswer.question_id ? newAnswer : a))
              : [...state.answers, newAnswer],
          })),
        goToNextQuestion: () => {
          const { currentQuestionIndex, questions, currentQuestionAnswer } = get();
          const currentQuestion = questions[currentQuestionIndex];
          if (!currentQuestion) return;
          const currentAnswer = currentQuestionAnswer[currentQuestion.id] || '';
          const answerStr = Array.isArray(currentAnswer) ? currentAnswer.join(',') : currentAnswer;
          // Save the answer for the current question.
          get().addOrUpdateAnswer({ question_id: currentQuestion.id, answer: answerStr });
          // Segment track for question answered
          get().trackQuestionAnswered(currentQuestion, currentAnswer);
          if (currentQuestionIndex < questions.length - 1) {
            const nextQuestion = questions[currentQuestionIndex + 1];
            const storedAnswer = getStoredAnswer(nextQuestion.id);
            set({
              currentQuestionIndex: currentQuestionIndex + 1,
              currentQuestionAnswer: {
                [nextQuestion.id]: storedAnswer.length ? storedAnswer.split(',') : [],
              },
            });
          }
        },
        goToPreviousQuestion: () => {
          const { currentQuestionIndex, questions } = get();
          if (currentQuestionIndex > 0) {
            const prevQuestion = questions[currentQuestionIndex - 1];
            const storedAnswer = getStoredAnswer(prevQuestion.id);
            set({
              currentQuestionIndex: currentQuestionIndex - 1,
              currentQuestionAnswer: {
                [prevQuestion.id]: storedAnswer.length ? storedAnswer.split(',') : [],
              },
            });
          }
        },
        clear: () =>
          set({
            answers: [],
            currentQuestionIndex: 0,
            currentQuestionAnswer: {},
          }),
        trackQuestionAnswered: (currentQuestion: IQuestion, currentAnswer: string[]) => {
          const selectedAnswerTexts = currentQuestion.answer_options
            ?.filter((option) => currentAnswer.includes(option.id.toString()))
            .map(({ answer_text }) => ({ answer_text }));
          segment.track('Pre Question Answered', {
            question_text: currentQuestion.question_text,
            user_answers: selectedAnswerTexts,
          });
        },
      };
    },
    { name: 'pre-questionnaire-storage' },
  ),
);
